import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { Checkbox } from "@shared/ui/checkbox";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { format } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { usePaymentMode } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface PaymentModeFormProps {
  form: UseFormReturn<any>;
  onFieldChange: (fieldName: string, value: any) => void;
  currency: string;
  totalPayableAmount: number;
  amountReceived: number;
  paymentMode: string;
  paymentType: string;
  createCreditNote: boolean;
  onCreateCreditNoteToggle: (checked: boolean) => void;
}

export const PaymentModeForm: React.FC<PaymentModeFormProps> = ({
  form,
  onFieldChange,
  currency,
  totalPayableAmount,
  amountReceived,
  paymentMode,
  paymentType,
  createCreditNote,
  onCreateCreditNoteToggle,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: paymentModes } = usePaymentMode(remoteUtilityId);

  const paymentModeList = Array.isArray(paymentModes)
    ? paymentModes
    : Array.isArray(paymentModes?.result)
      ? paymentModes.result
      : [];

  const currentModeName =
    paymentModeList?.find((m) => m.code === paymentMode)?.name?.toLowerCase() ||
    "";
  const isCheque = currentModeName.includes("cheque");
  const isCash = currentModeName.includes("cash");

  const cashReceivedValue = form.watch("cashReceived");
  const totalPaymentValue = form.watch("totalPayment");

  const summaryAmountReceived = isCash
    ? Number(cashReceivedValue || 0)
    : Number(totalPaymentValue || 0);

  // Calculate outstanding or refund
  const difference = summaryAmountReceived - totalPayableAmount;
  const isOutstanding = difference < 0;
  const isRefund = difference > 0;
  const amount = Math.abs(difference);

  const receivedAmountLabel = isCheque
    ? "Cheque Amount"
    : isCash
      ? "Cash Received"
      : "Amount Received";

  // Get selected payment mode configuration
  const paymentModeOptions = useMemo(
    () =>
      paymentModeList?.map((mode) => ({
        value: mode.code,
        label: mode.name,
      })),
    [paymentModeList],
  );

  const fields: FormField[] = useMemo(() => {
    const isMode = (values: any, modeName: string) => {
      const code = values?.paymentMode;
      const mode = paymentModeList?.find((m) => m.code === code);
      return mode?.name?.toLowerCase().includes(modeName.toLowerCase());
    };

    const currentModeName =
      paymentModeList
        ?.find((m) => m.code === paymentMode)
        ?.name?.toLowerCase() || "";
    const isCheque = currentModeName.includes("cheque");

    const shouldDisableAmount = String(paymentType) !== "2";

    const allFields: FormField[] = [
      {
        name: "paymentMode",
        label: "Select Payment Mode",
        type: "select",
        required: true,
        placeholder: "Select mode",
        options: paymentModeOptions,
        classes: FIELD_CLASSES,
        className: "col-span-12 lg:col-span-12",
      },
    ];

    if (isCheque) {
      allFields.push(
        {
          name: "chequeNumber",
          label: "Cheque Number",
          type: "text",
          maxLength: 12,
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "bankName",
          label: "Bank Name",
          type: "text",
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "accountHolderName",
          label: "Account Holder's Name",
          type: "text",
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "routingNumber",
          label: "Routing Number",
          type: "text",
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "bankAccountNumber",
          label: "Bank Account Number",
          type: "text",
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "chequeDate",
          label: "Cheque Date",
          type: "datepicker",
          defaultValue: format(new Date(), "yyyy-MM-dd"),
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
        {
          name: "totalPayment",
          label: "Cheque Amount",
          type: "number",
          min: 0,
          step: 0.01,
          disabled: shouldDisableAmount,
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cheque"),
        },
      );
    } else {
      allFields.push(
        {
          name: "totalPayment",
          label: "Amount",
          type: "number",
          min: 0,
          step: 0.01,
          disabled: shouldDisableAmount,
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => !!v.paymentMode,
        },
        {
          name: "cashReceived",
          label: "Cash Received",
          type: "number",
          min: 0,
          step: 0.01,
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "cash"),
        },
        {
          name: "referenceNumber",
          label: "Reference Number",
          type: "text",
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => isMode(v, "online"),
        },
        {
          name: "paymentDate",
          label: "Date",
          type: "datepicker",
          defaultValue: format(new Date(), "yyyy-MM-dd"),
          classes: FIELD_CLASSES,
          className: "col-span-12 md:col-span-6 lg:col-span-4",
          showWhen: (v) => !!v.paymentMode,
        },
      );
    }

    return allFields;
  }, [paymentModeOptions, paymentModeList, paymentMode, paymentType]);
  useEffect(() => {
    if (totalPayableAmount !== undefined) {
      form.setValue("totalPayment", totalPayableAmount);
      onFieldChange("totalPayment", totalPayableAmount);
    }
    const today = new Date().toISOString().split("T")[0];
    form.setValue("paymentDate", today);
    form.setValue("chequeDate", today);
    onFieldChange("paymentDate", today);
    onFieldChange("chequeDate", today);
  }, [totalPayableAmount, form]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-medium">
          3
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Payment Mode</h2>
      </div>

      {/* Payment Form (All Fields) */}
      <div className="mb-6">
        <DynamicForm
          fields={fields}
          form={form}
          onFieldChange={onFieldChange}
          gridColumns={12}
          config={{
            showSubmitButton: false,
            showResetButton: false,
            spacing: "medium",
          }}
        />
      </div>

      {/* Summary Section */}
      {amountReceived > 0 && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Payment Summary
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Payable Amount</span>
              <span className="font-medium text-gray-900">
                {currency} {totalPayableAmount}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{receivedAmountLabel}</span>
              <span className="font-medium text-gray-900">
                {currency} {summaryAmountReceived}
              </span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              {isOutstanding && (
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-red-700">
                    Outstanding
                  </span>
                  <span className="font-bold text-red-700">
                    {currency} {amount?.toFixed(2)}
                  </span>
                </div>
              )}
              {isRefund && (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-semibold text-green-700">
                      Refund / Excess Amount
                    </span>
                    <span className="font-bold text-green-700">
                      {currency} {Number(amount || 0).toFixed(2)}
                    </span>
                  </div>

                  {/* Create Credit Note Option */}
                  <div className="pt-3 border-t border-gray-300">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={createCreditNote}
                        onCheckedChange={onCreateCreditNoteToggle}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">
                          Create Credit Note
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          A credit note of {currency}{" "}
                          {Number(amount || 0).toFixed(2)} will be generated
                          and linked with this payment
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
              {difference === 0 && (
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-green-700">
                    Exact Payment
                  </span>
                  <span className="font-bold text-green-700">
                    {currency} 0.00
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
